import React from 'react'
import {
    Box,
    Card,
    CardContent,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { FunnelMetrics } from '../types/leasingSeason.types'

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        marginBottom: theme.spacing(2),
    },
    subtitle: {
        color: theme.palette.text.secondary,
        fontSize: '0.75rem',
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(2),
    },
    funnel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(0.75),
    },
    step: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.25, 2),
        borderRadius: theme.spacing(0.75),
        backgroundColor: theme.palette.primary.main + '12',
        border: `1px solid ${theme.palette.primary.main + '28'}`,
        transition: 'width 0.2s ease',
        minHeight: 44,
    },
    stepMuted: {
        backgroundColor: theme.palette.grey[100],
        border: `1px solid ${theme.palette.divider}`,
    },
    stepLabel: {
        fontSize: '0.8rem',
        fontWeight: 600,
        color: theme.palette.text.primary,
    },
    stepValue: {
        fontSize: '1.125rem',
        fontWeight: 700,
        color: theme.palette.primary.main,
        fontVariantNumeric: 'tabular-nums',
    },
    stepValueMuted: {
        color: theme.palette.text.secondary,
    },
    conversion: {
        fontSize: '0.65rem',
        color: theme.palette.text.secondary,
        textAlign: 'center',
        minHeight: 14,
    },
}))

interface ProspectingFunnelCardProps {
    metrics: FunnelMetrics
}

function conversionRate(from: number, to: number): string | null {
    if (from <= 0 || to <= 0) return null
    return `${Math.round((to / from) * 100)}% continue`
}

export const ProspectingFunnelCard: React.FC<ProspectingFunnelCardProps> = ({
    metrics,
}) => {
    const classes = useStyles()

    const steps = [
        {
            key: 'targets',
            label: 'Targets',
            value: metrics.prospects,
            live: true,
        },
        {
            key: 'prospects',
            label: 'Prospects',
            value: metrics.enriched,
            live: true,
        },
        {
            key: 'inCampaign',
            label: 'In campaigns',
            value: metrics.inCampaign,
            live: true,
        },
        {
            key: 'leads',
            label: 'Leads',
            value: metrics.leads,
            live: true,
        },
        {
            key: 'applications',
            label: 'Applications',
            value: metrics.applications,
            live: true,
        },
    ] as const

    const maxValue = Math.max(...steps.map((s) => s.value), 1)

    return (
        <Card className={classes.card} variant="outlined">
            <CardContent>
                <Typography variant="subtitle1">
                    <strong>Prospecting funnel</strong>
                </Typography>
                <Typography className={classes.subtitle}>
                    Served-school targets through enrichment, season campaigns,
                    and lead conversion
                </Typography>

                <Box className={classes.funnel}>
                    {steps.map((step, index) => {
                        const taper = 1 - index * 0.1
                        const volumeScale = step.value / maxValue
                        const widthPct = Math.min(
                            100,
                            Math.max(20, volumeScale * 100 * taper),
                        )
                        const prev = index > 0 ? steps[index - 1] : null
                        const rate =
                            prev && step.live
                                ? conversionRate(prev.value, step.value)
                                : null

                        return (
                            <React.Fragment key={step.key}>
                                {rate && (
                                    <Typography className={classes.conversion}>
                                        {rate}
                                    </Typography>
                                )}
                                <Box
                                    className={`${classes.step} ${
                                        !step.live ? classes.stepMuted : ''
                                    }`}
                                    style={{ width: `${widthPct}%` }}
                                >
                                    <Typography className={classes.stepLabel}>
                                        {step.label}
                                    </Typography>
                                    <Typography
                                        className={`${classes.stepValue} ${
                                            !step.live
                                                ? classes.stepValueMuted
                                                : ''
                                        }`}
                                    >
                                        {step.value.toLocaleString()}
                                    </Typography>
                                </Box>
                            </React.Fragment>
                        )
                    })}
                </Box>
            </CardContent>
        </Card>
    )
}
